// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type { AccountInfo, PublicClientApplication } from "@azure/msal-browser";
import { createLoginRequest, isAccountForConfiguredAuthority } from "../../authConfig";
import { navigateTo } from "../../router/navigation";
import { consumeReturnTo } from "../../routes/returnTo";
import {
  clearExpiredAuthCodeRetry,
  hasAttemptedExpiredAuthCodeRetry,
  markExpiredAuthCodeRetry,
} from "../../utils/expiredAuthCodeRetryGuard";
import { notifySignInFailed } from "../../utils/signInFailureSignal";
import { isOnSignInSurface } from "./signInHandoff";
import { clearExplicitSignOut } from "./signOutState";
import { clearRestartSignInAttempt } from "../../utils/restartSignInGuard";
import { noteDraftAccount } from "../../utils/draftOwner";

const REJECTED_SIGN_IN_CODES: readonly string[] = [
  "access_denied",
  "invalid_grant",
  "invalid_request",
  "invalid_client",
  "invalid_scope",
  "unauthorized_client",
  "server_error",
  "temporarily_unavailable",
  "login_required",
  "interaction_required",
  "consent_required",
];

export function isExpiredAuthorizationCodeError(error: unknown): boolean {
  const failure = error as
    | {
        errorCode?: unknown;
        errorMessage?: unknown;
        message?: unknown;
        errorCodes?: unknown;
      }
    | null
    | undefined;
  const code = typeof failure?.errorCode === "string" ? failure.errorCode : "";
  const detail = [failure?.errorMessage, failure?.message]
    .filter((value): value is string => typeof value === "string")
    .join("\n");
  if (detail.includes("AADSTS70008") || /authorization code[\s\S]*expired/i.test(detail)) {
    return true;
  }
  if (Array.isArray(failure?.errorCodes) && failure.errorCodes.includes(70008)) {
    return true;
  }
  return code === "invalid_grant" && /expired|70008/i.test(detail);
}

function rejectedSignInMessage(error: unknown): string | null {
  const failure = error as { errorCode?: unknown; errorMessage?: unknown } | null | undefined;
  const code = typeof failure?.errorCode === "string" ? failure.errorCode : "";
  if (!REJECTED_SIGN_IN_CODES.includes(code)) {
    return null;
  }
  const detail =
    typeof failure?.errorMessage === "string" ? failure.errorMessage.split(/\r?\n/)[0]?.trim() : "";
  return detail ? `Sign-in failed: ${detail}` : `Sign-in failed (${code}). Please try again.`;
}

function firstConfiguredAccount(
  preferred: AccountInfo | null | undefined,
  accounts: AccountInfo[],
): AccountInfo | null {
  if (preferred !== null && preferred !== undefined && isAccountForConfiguredAuthority(preferred)) {
    return preferred;
  }
  return accounts.find(isAccountForConfiguredAuthority) ?? null;
}

async function retryAfterExpiredAuthorizationCode(
  instance: PublicClientApplication,
): Promise<boolean> {
  if (hasAttemptedExpiredAuthCodeRetry()) {
    return false;
  }
  markExpiredAuthCodeRetry();
  await instance.loginRedirect(createLoginRequest());
  return true;
}

export async function completeInFlightRedirect(
  instance: PublicClientApplication,
): Promise<void> {
  try {
    const redirectResult = await instance.handleRedirectPromise();
    const account = firstConfiguredAccount(
      redirectResult?.account,
      instance.getAllAccounts(),
    );
    if (redirectResult?.account && isAccountForConfiguredAuthority(redirectResult.account)) {
      clearExplicitSignOut();
      clearRestartSignInAttempt();
      clearExpiredAuthCodeRetry();
    }
    if (account) {
      instance.setActiveAccount(account);
      noteDraftAccount(account.homeAccountId);
      if (isOnSignInSurface()) {
        navigateTo(consumeReturnTo() ?? "/insights");
      }
    }
  } catch (error) {
    if (isExpiredAuthorizationCodeError(error)) {
      const retried = await retryAfterExpiredAuthorizationCode(instance);
      if (retried) {
        return;
      }
    }
    const rejection = rejectedSignInMessage(error);
    if (rejection !== null) {
      notifySignInFailed(rejection);
      if (!isOnSignInSurface()) {
        navigateTo("/");
      }
      return;
    }
    const account = firstConfiguredAccount(null, instance.getAllAccounts());
    if (account) {
      instance.setActiveAccount(account);
      noteDraftAccount(account.homeAccountId);
      if (isOnSignInSurface()) {
        navigateTo(consumeReturnTo() ?? "/insights");
      }
    }
  }
}
